import { useState, useEffect, useRef } from "react";
import { Trash2, Loader2, MessageCircle, Calendar, Star, Bell, CheckCircle2, XCircle, Clock, Send, History, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminLayout } from "@/components/AdminLayout";
import { bookingsAPI, remindersAPI } from "@/lib/api";
import { toast } from "sonner";
import { format, parse } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function BookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [resendingId, setResendingId] = useState(null); // booking id currently being resent

  // Reminder state
  const [unscheduledCount, setUnscheduledCount] = useState(0);
  const [isReminderDialogOpen, setIsReminderDialogOpen] = useState(false);
  const [isProgressDialogOpen, setIsProgressDialogOpen] = useState(false);
  const [reminderDelay, setReminderDelay] = useState("30");
  const [isSendingReminders, setIsSendingReminders] = useState(false);
  const [reminderJob, setReminderJob] = useState(null); // { job_id, total, sent, failed, status, details }
  const pollRef = useRef(null);

  // History state
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyJobs, setHistoryJobs] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState(null);

  useEffect(() => {
    loadBookings();
    loadUnscheduledCount();
  }, []);

  // Stop polling when navigating away
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const loadBookings = async () => {
    try {
      const response = await bookingsAPI.list();
      setBookings(response.data);
    } catch (error) {
      toast.error("Erro ao carregar agendamentos");
    } finally {
      setIsLoading(false);
    }
  };

  const loadUnscheduledCount = async () => {
    try {
      const response = await remindersAPI.getUnscheduledCount();
      setUnscheduledCount(response.data.count);
    } catch {
      // silently ignore
    }
  };

  const handleDelete = async () => {
    if (!selectedBooking) return;

    try {
      await bookingsAPI.delete(selectedBooking.id);
      toast.success("Agendamento cancelado com sucesso!");
      setIsDeleteDialogOpen(false);
      setSelectedBooking(null);
      loadBookings();
      loadUnscheduledCount();
    } catch (error) {
      toast.error("Erro ao cancelar agendamento");
    }
  };

  const openDeleteDialog = (booking) => {
    setSelectedBooking(booking);
    setIsDeleteDialogOpen(true);
  };

  const handleResendWhatsapp = async (booking) => {
    setResendingId(booking.id);
    try {
      await bookingsAPI.resendWhatsapp(booking.id);
      toast.success(`✅ Mensagem reenviada para ${booking.child_name}!`);
      // Refresh bookings so whatsapp_notified updates to true
      loadBookings();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao reenviar mensagem";
      toast.error(msg);
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parse(dateStr, "yyyy-MM-dd", new Date()), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (isoStr) => {
    try {
      return format(new Date(isoStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return isoStr;
    }
  };

  // ---- REMINDER HANDLERS ----

  const handleOpenReminderDialog = () => {
    if (unscheduledCount === 0) {
      toast.info("Todos os pais já realizaram o agendamento! 🎉");
      return;
    }
    setIsReminderDialogOpen(true);
  };

  const handleOpenHistory = async () => {
    setIsHistoryOpen(true);
    setIsLoadingHistory(true);
    try {
      const res = await remindersAPI.getHistory();
      setHistoryJobs(res.data);
    } catch {
      toast.error("Erro ao carregar histórico");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSendReminders = async () => {
    setIsSendingReminders(true);
    try {
      const response = await remindersAPI.sendReminders({
        delay_seconds: parseInt(reminderDelay, 10),
      });
      const { job_id, total } = response.data;

      setReminderJob({ job_id, total, sent: 0, failed: 0, status: "pending", details: [] });
      setIsReminderDialogOpen(false);
      setIsProgressDialogOpen(true);

      // Start polling for job status
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await remindersAPI.getReminderStatus(job_id);
          const job = statusRes.data;
          setReminderJob({ job_id, ...job });

          if (job.status === "done") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            toast.success(`Lembretes enviados! ✅ ${job.sent} enviados, ${job.failed} falhas.`);
          }
        } catch {
          // polling errors are non-critical
        }
      }, 2000);
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao iniciar envio de lembretes";
      toast.error(msg);
    } finally {
      setIsSendingReminders(false);
    }
  };

  const handleCloseProgressDialog = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setIsProgressDialogOpen(false);
    setReminderJob(null);
  };

  const progressPercent = reminderJob
    ? Math.round(((reminderJob.sent + reminderJob.failed) / Math.max(reminderJob.total, 1)) * 100)
    : 0;

  const isDone = reminderJob?.status === "done";

  const defaultMessage = `Olá, {guardian_name}! 👋

Notamos que o pré-cadastro de *{child_name}* foi realizado, mas o agendamento ainda não foi confirmado.

Para garantir a vaga no evento de Robótica e Atleta Digital, acesse o link https://agendamento.javisacademy.com.br/verificar e complete o agendamento.

Qualquer dúvida, estamos à disposição! 😊`;

  return (
    <AdminLayout title="Agendamentos">
      <div className="space-y-8">
        {/* Header with Reminder + History Buttons */}
        <div className="flex justify-end gap-2">
          <Button
            onClick={handleOpenHistory}
            variant="outline"
            className="rounded-full gap-2 border-muted-foreground/30 text-muted-foreground hover:bg-muted"
            data-testid="history-button"
          >
            <History className="w-4 h-4" />
            Histórico
          </Button>
          <div className="relative">
            <Button
              onClick={handleOpenReminderDialog}
              variant="outline"
              className="rounded-full gap-2 border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
              data-testid="send-reminders-button"
            >
              <Bell className="w-4 h-4" />
              Enviar Lembretes
              {unscheduledCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-500 text-white text-xs font-bold">
                  {unscheduledCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : bookings.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Calendar className="w-12 h-12 mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground text-center">
                Nenhum agendamento realizado ainda
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 gap-6">
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Star className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total de Agendamentos</p>
                      <p className="text-2xl font-bold font-nunito">{bookings.length} agendamentos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Bookings Table */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="font-nunito">Todos os Agendamentos</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Criança</TableHead>
                        <TableHead>Evento</TableHead>
                        <TableHead>Data/Hora</TableHead>
                        <TableHead className="hidden md:table-cell">Confirmado em</TableHead>
                        <TableHead className="hidden md:table-cell">WhatsApp</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((booking) => (
                        <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`}>
                          <TableCell className="font-medium">{booking.child_name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Star className="w-4 h-4 text-primary" />
                              <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                Evento Completo
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{formatDate(booking.workshop_date)}</p>
                              <p className="text-sm text-muted-foreground">{booking.workshop_time}</p>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-muted-foreground">
                            {formatDateTime(booking.confirmed_at)}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {booking.whatsapp_notified ? (
                              <span className="flex items-center gap-1 text-emerald-600">
                                <MessageCircle className="w-4 h-4" />
                                <span className="text-xs">Enviado</span>
                              </span>
                            ) : (
                              <button
                                onClick={() => handleResendWhatsapp(booking)}
                                disabled={resendingId === booking.id}
                                className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 disabled:opacity-60 transition-colors"
                                title="Reenviar confirmação via WhatsApp"
                              >
                                {resendingId === booking.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Send className="w-3 h-3" />
                                )}
                                {resendingId === booking.id ? "Enviando..." : "Reenviar"}
                              </button>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {/* Resend button for mobile (WhatsApp column is hidden on small screens) */}
                              {!booking.whatsapp_notified && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleResendWhatsapp(booking)}
                                  disabled={resendingId === booking.id}
                                  className="rounded-full text-amber-500 hover:text-amber-600 md:hidden"
                                  title="Reenviar WhatsApp"
                                >
                                  {resendingId === booking.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openDeleteDialog(booking)}
                                className="rounded-full text-destructive hover:text-destructive"
                                data-testid={`cancel-booking-${booking.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-nunito">Cancelar Agendamento</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Tem certeza que deseja cancelar o agendamento de <strong>{selectedBooking?.child_name}</strong>?
            A vaga será liberada para outros participantes.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} className="rounded-full">
              Voltar
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-full" data-testid="confirm-cancel-booking">
              Cancelar Agendamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder Confirmation Dialog */}
      <Dialog open={isReminderDialogOpen} onOpenChange={setIsReminderDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-nunito flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Enviar Lembretes via WhatsApp
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Info card */}
            <div className="rounded-xl bg-orange-50 border border-orange-200 p-4">
              <p className="text-sm text-orange-800 font-medium">
                📊 <strong>{unscheduledCount}</strong> {unscheduledCount === 1 ? "responsável" : "responsáveis"} ainda não {unscheduledCount === 1 ? "realizou" : "realizaram"} o agendamento.
              </p>
              <p className="text-xs text-orange-600 mt-1">
                Os lembretes serão enviados apenas para esses responsáveis.
              </p>
            </div>

            {/* Delay selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                Intervalo entre mensagens
              </label>
              <Select value={reminderDelay} onValueChange={setReminderDelay}>
                <SelectTrigger className="h-10 rounded-xl" data-testid="reminder-delay-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 segundos (mais rápido)</SelectItem>
                  <SelectItem value="30">30 segundos (recomendado)</SelectItem>
                  <SelectItem value="60">60 segundos (mais seguro)</SelectItem>
                  <SelectItem value="120">2 minutos (muito seguro)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                ⚠️ Intervalos maiores reduzem o risco de bloqueio pelo WhatsApp.
              </p>
            </div>

            {/* Message preview */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Prévia da mensagem</label>
              <div className="rounded-xl bg-muted/50 border p-3 text-sm text-muted-foreground whitespace-pre-line font-mono text-xs leading-relaxed">
                {defaultMessage}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReminderDialogOpen(false)} className="rounded-full">
              Cancelar
            </Button>
            <Button
              onClick={handleSendReminders}
              disabled={isSendingReminders}
              className="rounded-full bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="confirm-send-reminders-button"
            >
              {isSendingReminders ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Iniciando...</>
              ) : (
                <><Bell className="w-4 h-4 mr-2" /> Enviar Lembretes</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress Modal */}
      <Dialog open={isProgressDialogOpen} onOpenChange={() => { }}>
        <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-nunito flex items-center gap-2">
              {isDone ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              ) : (
                <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
              )}
              {isDone ? "Lembretes Enviados!" : "Enviando Lembretes..."}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{reminderJob?.sent + reminderJob?.failed ?? 0} de {reminderJob?.total ?? 0}</span>
                <span>{progressPercent}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${isDone ? "bg-emerald-500" : "bg-orange-500"}`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-muted/50 border p-3 text-center">
                <p className="text-2xl font-bold text-foreground">{reminderJob?.total ?? 0}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600">{reminderJob?.sent ?? 0}</p>
                <p className="text-xs text-emerald-600">Enviados</p>
              </div>
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-center">
                <p className="text-2xl font-bold text-red-500">{reminderJob?.failed ?? 0}</p>
                <p className="text-xs text-red-500">Falhas</p>
              </div>
            </div>

            {/* Detail log */}
            {reminderJob?.details && reminderJob.details.length > 0 && (
              <div className="max-h-36 overflow-y-auto space-y-1 rounded-xl border p-2">
                {reminderJob.details.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                    {d.status === "sent" ? (
                      <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                    )}
                    <span className="truncate">{d.child}</span>
                    <span className="ml-auto text-muted-foreground/60">{d.phone}</span>
                  </div>
                ))}
              </div>
            )}

            {!isDone && (
              <p className="text-xs text-muted-foreground text-center">
                ⏱️ Aguarde — as mensagens são enviadas com intervalo para evitar bloqueio.
              </p>
            )}
          </div>

          <DialogFooter>
            {isDone ? (
              <Button onClick={handleCloseProgressDialog} className="rounded-full w-full">
                Fechar
              </Button>
            ) : (
              <Button variant="outline" onClick={handleCloseProgressDialog} className="rounded-full w-full">
                Fechar (continua em segundo plano)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== HISTORY MODAL ===== */}
      <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-nunito flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Histórico de Campanhas de Lembrete
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 py-2 pr-1">
            {isLoadingHistory ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : historyJobs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <History className="w-8 h-8 mb-2 opacity-40" />
                <p className="text-sm">Nenhuma campanha enviada ainda.</p>
              </div>
            ) : (
              historyJobs.map((job) => {
                const isExpanded = expandedJobId === job.id;
                const isRunning = job.status === "running" || job.status === "pending";
                const finishedAt = job.finished_at
                  ? (() => { try { return format(new Date(job.finished_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }); } catch { return job.finished_at; } })()
                  : null;
                const createdAt = job.created_at
                  ? (() => { try { return format(new Date(job.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }); } catch { return job.created_at; } })()
                  : "—";

                return (
                  <div key={job.id} className="rounded-xl border bg-card shadow-sm overflow-hidden">
                    {/* Job summary row */}
                    <button
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left"
                      onClick={() => setExpandedJobId(isExpanded ? null : job.id)}
                    >
                      {/* Status badge */}
                      <span className={`shrink-0 w-2 h-2 rounded-full ${isRunning ? "bg-orange-400 animate-pulse" :
                          job.failed > 0 ? "bg-amber-400" : "bg-emerald-500"
                        }`} />

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {isRunning ? (
                            <span className="text-orange-600">Em andamento...</span>
                          ) : (
                            <>Campanha de {createdAt}</>
                          )}
                        </p>
                        {finishedAt && (
                          <p className="text-xs text-muted-foreground">Concluído em {finishedAt}</p>
                        )}
                      </div>

                      {/* Stats chips */}
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                          {job.total} total
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                          ✓ {job.sent}
                        </span>
                        {job.failed > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">
                            ✗ {job.failed}
                          </span>
                        )}
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                      </div>
                    </button>

                    {/* Details list */}
                    {isExpanded && job.details && job.details.length > 0 && (
                      <div className="border-t bg-muted/30 max-h-52 overflow-y-auto">
                        {job.details.map((d, i) => (
                          <div key={i} className="flex items-center gap-2 px-4 py-2 text-xs border-b last:border-0">
                            {d.status === "sent" ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            ) : (
                              <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                            )}
                            <span className="flex-1 truncate text-foreground">{d.child}</span>
                            <span className="text-muted-foreground shrink-0">{d.phone}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {isExpanded && (!job.details || job.details.length === 0) && (
                      <p className="px-4 py-3 text-xs text-muted-foreground border-t">Sem detalhes disponíveis.</p>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setIsHistoryOpen(false)} className="rounded-full w-full">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
